import React from 'react';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';

export interface DisabledActionTooltipProps {
	disabled: boolean;
	tooltip?: string;
	children: React.ReactElement;
	/** Tailwind classes for the hover target wrapper. Defaults to `inline-flex`. */
	wrapperClassName?: string;
}

/**
 * Wraps an action control so a tooltip can explain why it is disabled.
 * Disabled controls ignore pointer events in the browser, so the child is marked
 * `pointer-events-none` and the wrapper captures hover/focus instead.
 *
 * @param {DisabledActionTooltipProps} props the wrapper props.
 * @returns {JSX.Element} the control, optionally wrapped in a tooltip.
 */
export function DisabledActionTooltip({
	disabled,
	tooltip,
	children,
	wrapperClassName = 'inline-flex',
}: DisabledActionTooltipProps) {
	if (!disabled || !tooltip) {
		return children;
	}

	const child = React.cloneElement(children, {
		className: [children.props.className, 'pointer-events-none'].filter(Boolean).join(' '),
		tabIndex: -1,
	});

	return (
		<TailwindTooltip
			reference={<span className={`${wrapperClassName} cursor-not-allowed`}>{child}</span>}
			displayClass={wrapperClassName}
			position='bottom'
			maxWidthClass='w-64'
		>
			{tooltip}
		</TailwindTooltip>
	);
}
